import React, { useCallback, forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Row, Col, UncontrolledTooltip } from "reactstrap";
import { Button } from "zina-dogi-react";
import swal from "sweetalert";
import { Formik, Form, Field } from "formik";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { service as oetService } from "../../oet/redux";
import { service as activityService } from "~/activity/redux";
import { utils, useHeaderTitle } from "~/utils";
import { loadSiteIdOptions, validate, formatSubmitPayload } from "../utils/site_id_autocomplete";

import "./ssot_web_header_form.component.css";

const DATE_FORMAT = "dd/MM/yyyy";
const HEADING_TITLE = "SSOT Web Form";
const DEFAULT_CLEARED_VALUES = {
    activity: null,
    customer_request_delivery_date: null,
    project_request_delivery_date: null,
    site_ids: []
};

/**
 * Form for SSOT creation.
 *
 * This form allows selecting an activity, delivery dates, and multiple Site IDs to filter the SSOT items.
 *
 * Features:
 * - **Context-Aware**: Site ID selection is enabled only when valid OET and Run contexts are provided.
 * - **Advanced Autocomplete**: Supports pasting multiple Site IDs and identifies codes that are not found.
 * - **Manual Entry**: Allows users to create custom Site IDs by typing values that don't exist in the autocomplete options.
 * - **State Management**: Uses key-based resetting to ensure third-party components (React-Select) clear state correctly.
 * - **Safety**: Implements confirmation dialogs for critical actions like clearing the form.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.oet - Current OET object (required).
 * @param {Object} props.run - Current Run object (required).
 * @param {Object} [props.initialValues=null] - Initial form values: { activity, customer_request_delivery_date, project_request_delivery_date, site_ids }.
 * @param {Function} [props.onSubmit] - Callback function triggered on form submission. Receives (values, formik).
 * @param {Function} [props.onClearForm] - Callback function triggered when form is cleared.
 * @param {string} [props.submitLabel="Submit"] - Label for the submit button.
 * @param {Function} [props.setheader] - Function to set the header title.
 * @param {boolean} [props.activityDisabled=false] - Whether the activity field should be disabled.
 * @param {boolean} [props.disabled=false] - Whether the entire form should be disabled.
 */
const SSOTWebHeaderForm = forwardRef(function SSOTWebHeaderForm({
    oet,
    run,
    initialValues: initialValuesProp = null,
    onSubmit,
    onClearForm,
    submitLabel = "Submit",
    setheader,
    activityDisabled = false,
    disabled = false
}, ref) {
    const [inputValueSite, setInputValueSite] = useState("");
    const [resetKey, setResetKey] = useState(0);
    const formikRef = useRef(null);

    useImperativeHandle(ref, () => ({
        clearSiteIds(idsToRemove) {
            if (!formikRef.current || !idsToRemove || idsToRemove.length === 0) return;
            const currentSiteIds = formikRef.current.values.site_ids || [];
            const filtered = currentSiteIds.filter(
                // opt.value may be a numeric DB id; opt.label holds the site code string.
                // The API may return either format, so check both.
                opt => !idsToRemove.includes(opt.value) && !idsToRemove.includes(opt.label)
            );
            formikRef.current.setFieldValue("site_ids", filtered, false);
            // Force AsyncCreatableSelect (react-select v2) to remount so it
            // reflects the updated value from Formik immediately.
            setResetKey(prev => prev + 1);
        }
    }));

    const hasContext = Boolean(oet && oet.id && run && run.id);

    // Determines the key for the site selection component.
    // Changing this key forces React to unmount and remount the AsyncCreatableSelect component.
    // This is necessary to reset its internal state (like loaded options) when the context (oet/run) changes.
    const siteSelectKey = useMemo(() => {
        if (!hasContext) return "site-select-disabled";
        return `site-select-${oet.id}-${run.id}`;
    }, [hasContext, oet, run]);

    useHeaderTitle(setheader, HEADING_TITLE);

    /**
     * Loads activity autocomplete options filtered by the OET's customer team.
     * Returns an empty Promise when OET context is not yet available.
     *
     * @param {string} val - The search string typed by the user.
     * @returns {Promise<Array<{value: number, label: string}>>} Matching activity options.
     */
    const loadActivityOptions = useCallback((val) => {
        if (!oet || !oet.cpo_data) return Promise.resolve([]);
        const ct = oet.customer_team_id;
        return activityService.getActivityAutocomplete(ct, val).then(resp =>
            resp.map(v => ({
                value: v.id,
                label: v.activity_name
            }))
        );
    }, [oet]);

    const initialValues = useMemo(
        () => initialValuesProp || DEFAULT_CLEARED_VALUES,
        [initialValuesProp]
    );

    return (
        <div className="ssot-creation-header-form">
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validate={validate}
                onSubmit={(values, formik) => {
                    const payload = formatSubmitPayload(values);
                    if (onSubmit) onSubmit(payload, formik);
                    formik.setSubmitting(false);
                }}
            >
                {formik => {
                    formikRef.current = formik;
                    const { values, setFieldValue, errors, touched } = formik;
                    const today = new Date();

                    return (
                        <Form className="form">
                            <div className="fields">
                                <Row>
                                    <Col sm="12" md="6">
                                        <div className="form-group">
                                            <label>Activity</label>
                                            <Field
                                                key={`activity-select-${resetKey}-${oet ? oet.id : 'waiting'}`}
                                                name="activity"
                                                value={values.activity}
                                                component={AsyncSelect}
                                                isClearable
                                                isDisabled={activityDisabled || disabled}
                                                cacheOptions
                                                defaultOptions={!!oet}
                                                loadOptions={val => loadActivityOptions(val)}
                                                onChange={val => setFieldValue("activity", val, false)}
                                                classNamePrefix="mi-select"
                                            />
                                            {errors.activity && touched.activity && (
                                                <div className="text-danger small">{errors.activity}</div>
                                            )}
                                        </div>
                                    </Col>

                                    <Col sm="12" md="6">
                                        <div className="form-group">
                                            <label>
                                                Site ID{" "}
                                                <i
                                                    id="site-id-tooltip-icon"
                                                    className="fas fa-info-circle text-muted"
                                                    style={{ cursor: "pointer" }}
                                                />
                                                <UncontrolledTooltip placement="top" target="site-id-tooltip-icon">
                                                    Please ensure the Site ID entered is correct and corresponds to the appropriate SMP Name in NDP-d
                                                </UncontrolledTooltip>
                                            </label>
                                            <Field
                                                key={`${siteSelectKey}-${resetKey}`}
                                                name="site_ids"
                                                value={values.site_ids}
                                                component={AsyncCreatableSelect}
                                                isClearable
                                                isDisabled={!hasContext || disabled}
                                                className="basic-multi-select"
                                                isMulti
                                                cacheOptions={false}
                                                classNamePrefix="mi-select"
                                                defaultOptions={hasContext}
                                                inputValue={inputValueSite}
                                                // Allow manual entry - user can type custom Site IDs
                                                allowCreateWhileLoading={true}
                                                createOptionPosition="first"
                                                formatCreateLabel={(inputValue) => `Use: "${inputValue}"`}
                                                loadOptions={v =>
                                                    loadSiteIdOptions(v, formik, {
                                                        hasContext,
                                                        oetId: null,
                                                        runId: null,
                                                        setInputValueSite,
                                                        splitText: utils.splitText,
                                                        getOETSitesAutoComplete: oetService.getOETSitesAutoComplete
                                                    })
                                                }
                                                onInputChange={(v, { action }) => {
                                                    // Only update when typing or pasting
                                                    if (action === "input-change" || action === "menu-close") {
                                                        setInputValueSite(v);
                                                    }
                                                    if (action === "set-value") {
                                                        setInputValueSite("");
                                                    }
                                                }}
                                                onChange={v => {
                                                    setFieldValue("site_ids", v || [], false);
                                                }}
                                                // Handle creation of new options from manual input
                                                onCreateOption={(inputValue) => {
                                                    const newOption = {
                                                        value: inputValue,
                                                        label: inputValue,
                                                        __isNew__: true
                                                    };
                                                    setFieldValue("site_ids", [...(values.site_ids || []), newOption], false);
                                                    setInputValueSite("");
                                                }}
                                            />
                                            {errors.site_ids && touched.site_ids && (
                                                <div className="text-danger small">{errors.site_ids}</div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="12" md="6">
                                        <div className="form-group">
                                            <label>Customer request delivery date</label>
                                            <Field
                                                component={DatePicker}
                                                dateFormat={DATE_FORMAT}
                                                disabled={disabled}
                                                onChange={value =>
                                                    setFieldValue("customer_request_delivery_date", value, false)
                                                }
                                                name="customer_request_delivery_date"
                                                autoComplete="off"
                                                minDate={today}
                                                selected={values.customer_request_delivery_date}
                                                className="form-control"
                                            />
                                            {errors.customer_request_delivery_date &&
                                                touched.customer_request_delivery_date && (
                                                    <div className="text-danger small">
                                                        {errors.customer_request_delivery_date}
                                                    </div>
                                                )}
                                        </div>
                                    </Col>
                                    <Col sm="12" md="6">
                                        <div className="form-group">
                                            <label>Project Request delivery date</label>
                                            <Field
                                                component={DatePicker}
                                                dateFormat={DATE_FORMAT}
                                                disabled={disabled}
                                                onChange={value =>
                                                    setFieldValue("project_request_delivery_date", value, false)
                                                }
                                                name="project_request_delivery_date"
                                                autoComplete="off"
                                                minDate={today}
                                                selected={values.project_request_delivery_date}
                                                className="form-control"
                                            />
                                            {errors.project_request_delivery_date &&
                                                touched.project_request_delivery_date && (
                                                    <div className="text-danger small">
                                                        {errors.project_request_delivery_date}
                                                    </div>
                                                )}
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="controls">
                                    <Button type="submit" color="primary" className="btn-sm mr-1" disabled={disabled || formik.isSubmitting}>
                                        {submitLabel}
                                    </Button>
                                    <Button
                                        type="button"
                                        color="danger"
                                        className="btn-sm mr-1"
                                        disabled={disabled}
                                        onClick={() => {
                                            swal({
                                                title: "Are you sure?",
                                                text: "All data in the form will be cleared.",
                                                icon: "warning",
                                                buttons: true,
                                                dangerMode: true,
                                            }).then((willDelete) => {
                                                if (willDelete) {
                                                    formik.resetForm({
                                                        values: DEFAULT_CLEARED_VALUES
                                                    });
                                                    setInputValueSite("");
                                                    setResetKey(prev => prev + 1);
                                                    if (onClearForm) onClearForm();
                                                }
                                            });
                                        }}
                                    >
                                        Clear Form
                                    </Button>
                                </Row>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
});

export default SSOTWebHeaderForm;
